import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiVersions } from 'src/app/shared/services/api-version';
import { BaseService } from 'src/app/shared/services/base.service';
import { Person } from '../models/person';
import { TeamHistory } from '../models/teamHistory';
import { Image } from '../models/image';
import { Note } from 'src/app/people/models/note';
import { PersonEvaluationSummary } from 'src/app/shared/models/evaluation/personEvaluationSummary';
import { Education } from '../models/education';
import { PersonCertification } from '../models/personCertification';
import {
  PersonCertificationComparator,
  StringIgnoreCaseComparator,
} from 'src/app/shared/utils/comparators';
import { PersonApplication } from 'src/app/shared/models/application/personApplication';
import { PersonEvaluationResponsesDetail } from 'src/app/shared/models/evaluation/personEvaluationResponsesDetail';
import { EvaluationFilterResult } from 'src/app/shared/models/evaluation/evaluationFilterResult';
import { IEvaluationQueryModel } from 'src/app/shared/models/evaluation/evaluationQueryModel';
import { nameOf } from 'src/app/shared/utils/name-of';
import { PersonCertificationWrite } from '../models/personCertificationWrite';
import { Certification } from '../models/certification';
import { IPersonInvoiceModel, PersonInvoiceModel } from './profile-payments/person-invoice-model';
import { tomorrow } from 'src/app/shared/utils/date-utils';
import { PersonCredential } from 'src/app/shared/models/credential/personCredential';
import {
  PersonCredentialScenario,
  PersonCredentialScenarioStatus,
} from 'src/app/shared/models/credential/personCredentialScenario';
import { CredentialStatus } from 'src/app/shared/models/credential/credentialStatus';
import { PersonScenarioRequirement } from 'src/app/shared/models/credential/personScenarioRequirement';
import { IInvoiceModel } from 'src/app/shared/models/credential/invoice.model';
import {
  PersonCredentialPatchDatesModel,
  UpdateCredentialDateType,
} from 'src/app/shared/models/credential/personCredentialPatchDates.model';
import { HistoryModel } from '../models/history';
import { IAmEvidenceDetails } from 'src/app/shared/models/credential/personScenarioEvidenceDetails';
import { EntityCredentialSpecialty } from 'src/app/shared/models/credential/entityCredentialSpecialty';
import { EntityCredentialSpecialtyEdit } from 'src/app/shared/models/credential/entityCredentialSpecialtyEdit';
import { EntityCredentialApplication } from 'src/app/shared/models/credential/entityCredentialApplication';
import { IEntityCredentialPaymentDetails } from 'src/app/shared/models/credential/entityCredentialPayment';
import { EntityCredentialSpecialtyEvidenceModel } from 'src/app/shared/models/credential/entityCredentialSpecialtyEvidenceModel.model';

@Injectable({
  providedIn: 'root',
})
export class PersonProfileService extends BaseService {
  private baseUsersPath: string = 'users';

  public readonly person = new BehaviorSubject<Person>(null);
  public readonly forms = new BehaviorSubject<PersonApplication[]>([]);
  public readonly personTeamHistory = new BehaviorSubject<TeamHistory[]>(null);
  public readonly personEvaluationSummary = new BehaviorSubject<PersonEvaluationSummary[]>([]);
  public readonly teamEvaluationResponsesAggregate = new Subject<PersonEvaluationResponsesDetail>();
  public readonly educations = new BehaviorSubject<Education[]>([]);
  public readonly notes = new BehaviorSubject<Note[]>([]);
  public readonly allEvidenceTypes = new BehaviorSubject<IAmEvidenceDetails>(null);
  public readonly credentialSpecialtyEvidences = new BehaviorSubject<EntityCredentialSpecialtyEvidenceModel>(null);

  public readonly personCertifications = new BehaviorSubject<PersonCertification[]>([]);
  public readonly personCredentials = new BehaviorSubject<PersonCredential[]>([]);
  public personCredentialScenario: ReplaySubject<PersonCredentialScenario> =
    new ReplaySubject<PersonCredentialScenario>();

  public readonly personCredentialApplications = new BehaviorSubject<EntityCredentialApplication[]>(
    null
  );

  public readonly personCredentialScenarioDeleted = new Subject<string>();
  public readonly personStatus = new BehaviorSubject<string>(null);

  constructor() {
    super();
  }

  private baseCertificationsPath: string = 'certifications';

  private usersBaseUrl(userId: string) {
    return `${this.baseUsersPath}/${userId}`;
  }

  private getPersonTeamHistoryUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/teams');
  }

  private getEvaluationsSummaryUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/evaluations');
  }

  private getPersonImageUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/image');
  }

  private getPersonEducationUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/educations');
  }

  private getPersonCertificationsUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/certifications');
  }

  private getApplicationsUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/applications');
  }

  private getPersonCredentialsUrl(userId: string) {
    return this.usersBaseUrl(userId).concat(`/credentials`);
  }

  private getPersonCredentialScenariosUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/scenarios');
  }

  private getPersonCredentialApplicationsUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/applications');
  }

  private getPersonRequirementUrl = (
    userId: string,
    personScenarioId: string,
    personRequirementId: string,
    resource: string = null
  ) =>
    resource
      ? `${this.usersBaseUrl(
          userId
        )}/scenarios/${personScenarioId}/requirements/${personRequirementId}/${resource}`
      : `${this.usersBaseUrl(
          userId
        )}/scenarios/${personScenarioId}/requirements/${personRequirementId}`;

  private convertApplications(data): PersonApplication[] {
    return data.map((application) => PersonApplication.parse(application));
  }

  private convertPerson(data): Person {
    if (!data) return null;
    return new Person(data);
  }

  private convertTeamHistoryData(data): TeamHistory[] {
    return data.map((teamHistory) => new TeamHistory(teamHistory));
  }

  private convertEvaluationsSummaryData(data): PersonEvaluationSummary[] {
    return data.map((evaluationSummary) => PersonEvaluationSummary.parse(evaluationSummary));
  }

  private convertEducationData(data): Education[] {
    return data.map((education) => new Education(education));
  }

  private convertPersonCertificationsData(data): PersonCertification[] {
    return data.map((personCertification) => new PersonCertification(personCertification));
  }

  private orderPersonCertifications(data): PersonCertification[] {
    return data.sort(PersonCertificationComparator);
  }

  private convertEvaluationResponseDetailData(data) {
    if (data) {
      let evaluationODataResult = new EvaluationFilterResult().parse(data);
      return PersonEvaluationResponsesDetail.parse(evaluationODataResult);
    }
  }

  private convertPersonCredentialsData(data): PersonCredential[] {
    return data.map((personCredential) => PersonCredential.parse(personCredential));
  }

  private convertPersonCredentialApplicationsData(data): EntityCredentialApplication[] {
    return data.map((credentialApps) => EntityCredentialApplication.parse(credentialApps));
  }

  private getPersonNotesUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/notes');
  }

  private getPersonRatingUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/update-rating');
  }

  private getTeamEvaluationResponsesDetailUrl(userId: string, teamId: string) {
    const filter = `$filter=${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluateeId
    )} eq ${userId} and ${nameOf<IEvaluationQueryModel>(
      (e) => e.teamId
    )} eq ${teamId} and responses/any()`;
    const select = `$select=${nameOf<IEvaluationQueryModel>(
      (e) => e.teamName
    )}, ${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluateePosition
    )}, ${nameOf<IEvaluationQueryModel>((e) => e.evaluationName)}, ${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluationResponseDate
    )}`;
    const expand = `$expand=${nameOf<IEvaluationQueryModel>((e) => e.responses)}`;
    return `query/evaluationMaxResult?${filter}&${select}&${expand}&$count=true`;
  }

  private getEvaluationResponsesDetailUrl(userId: string, evaluationFormId) {
    const filter = `$filter=${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluateeId
    )} eq ${userId} and ${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluationFormId
    )} eq ${evaluationFormId} and ${nameOf<IEvaluationQueryModel>((e) => e.responses)}/any()`;
    const select = `$select=${nameOf<IEvaluationQueryModel>(
      (e) => e.teamName
    )}, ${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluateePosition
    )}, ${nameOf<IEvaluationQueryModel>((e) => e.evaluationName)}, ${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluationResponseDate
    )}`;
    const expand = `$expand=${nameOf<IEvaluationQueryModel>((e) => e.responses)}`;
    return `query/evaluationMaxResult?${filter}&${select}&${expand}&$count=true`;
  }

  getPerson = (personId: string, ok404: boolean = true): Promise<Person> =>
    new Promise((resolve, reject) => {
      this.get(this.usersBaseUrl(personId), ok404, ApiVersions.v1_0)
        .pipe(map(this.convertPerson))
        .subscribe((data) => {
          if (data) {
            this.person.next(data);
            resolve(data);
          } else {
            const error = { message: 'Person not found', status: 404 };
            reject(reject);
            this.person.error(error);
          }
        });
    });

  getForms(userId: string) {
    this.get(this.getApplicationsUrl(userId), true)
      .pipe(map(this.convertApplications))
      .subscribe((data) => {
        if (data) {
          this.forms.next(data);
        }
      });
  }

  getPersonTeamHistory(personId: string) {
    return this.get(this.getPersonTeamHistoryUrl(personId), true, ApiVersions.v1_0)
      .pipe(map(this.convertTeamHistoryData))
      .subscribe((data) => {
        if (data) {
          this.personTeamHistory.next(data);
        }
      });
  }

  getEvaluationsSummary(userId: string) {
    this.get(this.getEvaluationsSummaryUrl(userId), true)
      .pipe(map(this.convertEvaluationsSummaryData))
      .subscribe((data) => {
        if (data) {
          this.personEvaluationSummary.next(data);
        }
      });
  }

  getTeamEvaluationResponsesAggregate(userId: string, teamId: string) {
    this.get(
      this.getTeamEvaluationResponsesDetailUrl(userId, teamId),
      true,
      ApiVersions.unversioned
    )
      .pipe(map(this.convertEvaluationResponseDetailData))
      .subscribe((result) => {
        if (result) {
          this.teamEvaluationResponsesAggregate.next(result);
        }
      });
  }
  getPersonEducation(userId: string) {
    this.get(this.getPersonEducationUrl(userId), true)
      .pipe(map(this.convertEducationData))
      .subscribe((data) => {
        if (data) {
          this.educations.next(data);
        }
      });
  }

  getPersonCertifications(userId: string) {
    this.get(this.getPersonCertificationsUrl(userId), false, ApiVersions.v1_0)
      .pipe(map(this.convertPersonCertificationsData))
      .pipe(map(this.orderPersonCertifications))
      .subscribe((data) => {
        if (data) {
          this.personCertifications.next(data);
        }
      });
  }

  getPersonCredentials(userId: string) {
    this.get(this.getPersonCredentialsUrl(userId), false, ApiVersions.v1_0)
      .pipe(map(this.convertPersonCredentialsData))
      .subscribe((data) => {
        if (data) {
          this.personCredentials.next(data);
        }
      });
  }

  getPersonAvailableCredentialsAutomations = (userId: string): Promise<AutomationResults> =>
    this.get(`${this.getPersonCredentialsUrl(userId)}/automations`, false, ApiVersions.v1_0)
      .pipe(map((jsonResult) => jsonResult))
      .toPromise();

  queuePersonAvailableCredentialsAutomations = (userId: string) =>
    this.post(
      `${this.getPersonCredentialsUrl(userId)}/automations`,
      null,
      null,
      ApiVersions.v1_0
    ).toPromise();

  getPersonCredentialScenario = (userId: string, personCredentialScenarioId: string) =>
    this.get(
      `${this.getPersonCredentialScenariosUrl(userId)}/${personCredentialScenarioId}`,
      false,
      ApiVersions.v1_0
    )
      .toPromise()
      .then((json) => this.parseScenarioAndUpdateObservable(json));

  private parseScenarioAndUpdateObservable(json: any) {
    const model = PersonCredentialScenario.parseWithRequirement(json);
    this.personCredentialScenario.next(model);
    return model;
  }

  getPersonCredentialApplications = (userId: string) =>
    this.get(this.getPersonCredentialApplicationsUrl(userId), false, ApiVersions.v1_0)
      .toPromise()
      .then((json) => {
        const model = this.convertPersonCredentialApplicationsData(json);
        this.personCredentialApplications.next(model);
        return model;
      });

  getPersonCredentialApplicationDetails(
    userId: string,
    personCredentialApplicationId: string
  ): Promise<EntityCredentialApplication> {
    const path = this.getPersonCredentialApplicationsUrl(userId).concat(
      `/${personCredentialApplicationId}`
    );
    return this.get(path, false, ApiVersions.v1_0)
      .pipe(map((app) => EntityCredentialApplication.parse(app)))
      .toPromise();
  }

  getPersonCredentialApplicationPaymentDetails = (
    userId: string,
    personCredentialApplicationId: string,
    credentialPaymentId: string
  ): Promise<IEntityCredentialPaymentDetails> =>
    this.get(
      this.getPersonCredentialApplicationsUrl(userId).concat(
        `/${personCredentialApplicationId}/payments/${credentialPaymentId}`
      ),
      false,
      ApiVersions.v1_0
    ).toPromise();

  updatePersonCredentialApplicationPayment = (
    userId: string,
    personCredentialApplicationId: string,
    credentialPaymentId: string,
    checkSubmitted: CheckSubmitted
  ): Promise<boolean> =>
    this.put(
      this.getPersonCredentialApplicationsUrl(userId).concat(
        `/${personCredentialApplicationId}/payments/${credentialPaymentId}`
      ),
      JSON.stringify(checkSubmitted),
      null,
      ApiVersions.v1_0
    ).toPromise();

  addPersonCredentialApplicationScenario(
    userId: string,
    personCredentialApplicationId: string,
    scenarioDefinitionId
  ): Promise<EntityCredentialApplication> {
    const path = `${this.getPersonCredentialApplicationsUrl(
      userId
    )}/${personCredentialApplicationId}/scenarios/${scenarioDefinitionId}`;
    return this.post(path, null, null, ApiVersions.v1_0)
      .pipe(map((app) => EntityCredentialApplication.parse(app)))
      .toPromise();
  }

  editPersonCredentialSpecialty(
    userId: string,
    personCredentialApplicationId: string,
    model: EntityCredentialSpecialtyEdit
  ): Promise<EntityCredentialSpecialty> {
    const path = `${this.getPersonCredentialApplicationsUrl(
      userId
    )}/${personCredentialApplicationId}/specialty`;

    return this.put(path, JSON.stringify(model), null, ApiVersions.v1_0)
      .pipe(map((data) => EntityCredentialSpecialty.parse(data)))
      .toPromise();
  }

  async deletePersonCredentialSpecialty(
    userId: string,
    personCredentialSpecialtyId: string
  ): Promise<boolean> {
    const path = `${this.getPersonCredentialApplicationsUrl(
      userId
    )}/specialty/${personCredentialSpecialtyId}`;
    return await this.delete(path, null, ApiVersions.v1_0).toPromise();
  }

  completeRequirement(
    userId: string,
    personScenarioId: string,
    personRequirementId: string,
    payload: MarkScenarioComplete
  ): Promise<PersonScenarioRequirement> {
    const url = this.getPersonRequirementUrl(userId, personScenarioId, personRequirementId);

    return this.put(url, JSON.stringify(payload), null, ApiVersions.v1_0)
      .pipe(map((jsonResult) => PersonScenarioRequirement.parse(jsonResult)))
      .toPromise();
  }

  getPersonScenarioEvidences = async (
    personId: string,
    personScenarioId: string,
    personRequirementId: string
  ): Promise<IAmEvidenceDetails> =>
    await this.get(
      this.getPersonRequirementUrl(personId, personScenarioId, personRequirementId, 'evidences'),
      false,
      ApiVersions.v1_0
    )
      .toPromise()
      .then((result) => {
        this.allEvidenceTypes.next(result);
        return result;
      });

  async getSpecialtyEvidence(
    userId: string,
    personCredentialApplicationId: string,
  ): Promise<EntityCredentialSpecialtyEvidenceModel> {
    const path = `${this.getPersonCredentialApplicationsUrl(
      userId
    )}/${personCredentialApplicationId}/specialty/evidence`;

    return await this.get(path, null, ApiVersions.v1_0)
      .toPromise()
      .then(result => {
        this.credentialSpecialtyEvidences.next(result);
        return result;
      });
  }

  addPersonCredentialApplication(userId: string, credentialApplicationDefinitionId: string) {
    let body = JSON.stringify({
      credentialApplicationDefinitionId: credentialApplicationDefinitionId,
    });
    return this.post(
      this.getPersonCredentialApplicationsUrl(userId),
      body,
      null,
      ApiVersions.v1_0
    ).pipe(
      map((result) => {
        if (!result) {
          return false;
        }
        this.personCredentialApplications.next([
          ...this.personCredentialApplications.value,
          EntityCredentialApplication.parse(result.application),
        ]);
        if (
          result.credential &&
          this.personCredentials.value.findIndex((x) => x.id == result.credential.id) == -1
        ) {
          this.personCredentials.next([
            ...this.personCredentials.value,
            PersonCredential.parse(result.credential),
          ]);
        }
        return true;
      })
    );
  }

  patchPersonImageId(userId: string, imageId: string) {
    this.patch(this.getPersonImageUrl(userId), { imageId: imageId }).subscribe(() => {
      this.person.value.image = new Image(imageId);
    });
  }

  getPersonNotes(userId: string) {
    this.get(this.getPersonNotesUrl(userId), false, ApiVersions.v1_0).subscribe((data) => {
      if (data) {
        this.notes.next(data);
      }
    });
  }

  deletePersonNote(userId: string, noteId: string): Observable<any> {
    let deleteUrl = this.getPersonNotesUrl(userId).concat(`/${noteId}`);
    return this.delete(deleteUrl, null, ApiVersions.v1_0);
  }

  createPersonNote(userId: string, note: Note) {
    let newNoteStream = JSON.stringify(note);
    return this.post(this.getPersonNotesUrl(userId), newNoteStream, null, ApiVersions.v1_0).pipe(
      map((note) => {
        return new Note(note);
      })
    );
  }

  public canDeleteNote(note: Note): boolean {
    return (
      !note.actionType &&
      (this.authService.ownerLevelAccess ||
        (note.personId != null && note.personId == this.authService.userProfile?.userId))
    );
  }

  setStatus(userId: string, status: string) {
    let path = `${this.usersBaseUrl(userId)}/status?status=${status}`;
    this.patch(path).subscribe((result) => {
      if (result) {
        this.person.value.status = status;
        this.personStatus.next(status);
      }
    });
  }

  getEvaluationResponsesDetail(
    userId: string,
    evaluationFormId: string
  ): Observable<PersonEvaluationResponsesDetail> {
    return this.get(
      this.getEvaluationResponsesDetailUrl(userId, evaluationFormId),
      true,
      ApiVersions.unversioned
    ).pipe(map(this.convertEvaluationResponseDetailData));
  }

  getCertifications(): Observable<Certification[]> {
    return this.get(this.baseCertificationsPath, true)
      .pipe(map(this.convertCertificationsData))
      .pipe(map(this.orderCertifications));
  }

  savePersonCertification(
    userId: string,
    personCertificationId: string,
    personCertification: PersonCertificationWrite
  ): Observable<PersonCertification> {
    return personCertificationId
      ? this.updatePersonCertification(userId, personCertificationId, personCertification)
      : this.createPersonCertification(userId, personCertification);
  }

  patchPersonMetadata(personId: string, extSysId: string, successMessage: string) {
    let path = `${this.usersBaseUrl(personId)}/metadata`;
    this.patch(path, { externalSystemId: extSysId }, ApiVersions.v1_0).subscribe((result) => {
      if (result) {
        this.person.value.metadata = result.metadata;
        this.person.next(this.person.value);
        this.openSnackBar(successMessage, true);
      }
    });
  }

  getPersonInvoices(personId: string): Observable<PersonInvoiceModel[]> {
    return this.get(`payment/${personId}/invoices`, true, ApiVersions.unversioned).pipe(
      map((data) => data.map((invoice) => new PersonInvoiceModel(invoice)))
    );
  }

  getPaymentPaymentConfirmation = async (
    personId: string,
    personInvoiceId: string
  ): Promise<IInvoiceModel> => {
    return await this.get(
      `payment/${personId}/invoices/${personInvoiceId}`,
      {},
      ApiVersions.unversioned
    ).toPromise();
  };

  sendInvoice(personId: string) {
    let path = `payment/${personId}/invoices/send?dueDate=${tomorrow()}`;
    return this.post(path, null, null, ApiVersions.unversioned);
  }

  payInvoiceByCheck = (personId: string, checkSubmitted: CheckSubmitted) => {
    return this.put(
      `payment/${personId}/invoice/check`,
      JSON.stringify(checkSubmitted),
      null,
      ApiVersions.unversioned
    )
      .pipe(
        map((json: IPersonInvoiceModel) => {
          if (checkSubmitted.personRequirementId && checkSubmitted.personScenarioId) {
            return PersonScenarioRequirement.parse(json);
          }
          return new PersonInvoiceModel(json);
        })
      )
      .toPromise<PersonInvoiceModel | PersonScenarioRequirement>();
  };
  deletePersonCredentialScenario(
    userId: string,
    personCredentialScenarioId: string
  ): Promise<boolean> {
    return this.delete(
      `${this.getPersonCredentialScenariosUrl(userId)}/${personCredentialScenarioId}`,
      null,
      ApiVersions.v1_0
    )
      .pipe(
        map(() => {
          this.personCredentialScenario.next(null);

          this.personCredentialScenarioDeleted.next(personCredentialScenarioId);
          return true;
        })
      )
      .toPromise();
  }

  updatePersonCredentialScenario(
    userId: string,
    personCredentialScenarioId: string,
    payload: { status: PersonCredentialScenarioStatus; note?: string }
  ): Promise<boolean> {
    return this.put(
      `${this.getPersonCredentialScenariosUrl(userId)}/${personCredentialScenarioId}`,
      JSON.stringify(payload),
      null,
      ApiVersions.v1_0
    )
      .pipe(
        map((json) => {
          const model = this.parseScenarioAndUpdateObservable(json);
          if (model.status !== 'Completed') {
            return true;
          }

          //scenario was completed, update the credential
          this.personCredentials.next([
            ...this.personCredentials.value.map((c) => {
              if (c.id === model.personCredentialId) {
                c.status = CredentialStatus.Active;
              }
              return c;
            }),
          ]);
          return true;
        })
      )
      .toPromise();
  }

  async updateScenarioTags(personCredentialScenario: PersonCredentialScenario) {
    return this.put(
      `${this.getPersonCredentialScenariosUrl(personCredentialScenario.entityId)}/${
        personCredentialScenario.id
      }/tags`,
      JSON.stringify(personCredentialScenario.tags),
      null,
      ApiVersions.v1_0
    ).subscribe(() => {
      this.getPersonCredentialScenario(
        personCredentialScenario.entityId,
        personCredentialScenario.id
      );
    });
  }

  private convertCertificationsData(data) {
    return data.map((certification) => new Certification(certification));
  }

  private orderCertifications(data) {
    return data.sort(StringIgnoreCaseComparator('name'));
  }

  private createPersonCertification(
    userId: string,
    personCertification: PersonCertificationWrite
  ): Observable<PersonCertification> {
    let body = JSON.stringify(personCertification);
    return this.post(this.getPersonCertificationsUrl(userId), body, null, ApiVersions.v1_0).pipe(
      map(this.convertPersonCertificationData)
    );
  }

  private updatePersonCertification(
    userId: string,
    personCertificationId: string,
    personCertification: PersonCertificationWrite
  ): Observable<PersonCertification> {
    let body = JSON.stringify(personCertification);
    return this.put(
      this.getPersonCertificationEditDeleteUrl(userId, personCertificationId),
      body,
      null,
      ApiVersions.v1_0
    ).pipe(map(this.convertPersonCertificationData));
  }

  private getPersonCertificationEditDeleteUrl(userId: string, certificationId: string) {
    return this.getPersonCertificationsUrl(userId).concat(`/${certificationId}`);
  }

  private convertPersonCertificationData(data) {
    return new PersonCertification(data);
  }

  getApplications(userId: string): Observable<PersonApplication[]> {
    return this.get(this.getApplicationsUrl(userId), true).pipe(map(this.convertApplications));
  }

  patchRating(userId: string) {
    this.patch(this.getPersonRatingUrl(userId));
  }

  deletePerson(person: Person) {
    this.delete(this.usersBaseUrl(person.id), null, ApiVersions.v0_1, true).subscribe(
      () => {
        this.openSnackBar(`Successfully deleted ${person.fullName}.`, true);
      },
      (error) => {
        if (error?.status == 400 && error?.error) {
          this.openSnackBar(`${error.error}`);
        } else {
          this.openSnackBar(`Could not delete person.`);
        }
      }
    );
  }

  sendForm(userId: string, formId: string): Observable<any> {
    return this.post(
      `${this.baseUsersPath}/${userId}/applications/${formId}/assign`,
      null,
      null,
      ApiVersions.v1_0
    );
  }

  changeManualStatus = (
    userId: string,
    personCredentialId: string,
    manualStatusId: string,
    note: string
  ): Promise<boolean> =>
    this.patch(
      `${this.getPersonCredentialsUrl(userId)}/${personCredentialId}/change-manual-status`,
      JSON.stringify({
        manualStatusId: manualStatusId,
        note: note,
      }),
      ApiVersions.v0_1
    ).toPromise();

  updateCredentialDate(
    userId: string,
    personCredentialId: string,
    model: PersonCredentialPatchDatesModel
  ) {
    let path = this.getPersonCredentialsUrl(userId).concat(`/${personCredentialId}/change-date`);
    let body = JSON.stringify(model);

    return this.patch(path, body, ApiVersions.v0_1)
      .pipe(
        map((res: boolean) => {
          if (res) {
            this.openSnackBar(
              `${
                model.updateCredentialDateType == UpdateCredentialDateType.IssueDate
                  ? 'Issue date'
                  : 'Expiration date'
              } successfully updated.`,
              true
            );
            return true;
          }
        }),
        catchError((err) => {
          this.openSnackBar(err.error, false);
          return of(false);
        })
      )
      .toPromise();
  }

  getPersonScenarioHistory = (
    personId: string,
    personScenarioId: string
  ): Promise<HistoryModel[]> =>
    this.get(
      `${this.usersBaseUrl(personId)}/scenarios/${personScenarioId}/history`,
      false,
      ApiVersions.v1_0
    ).toPromise();

  generateCredentialInvoice = (userId: string, credentialFeeDefinitionId: string) =>
    this.post(
      `payment/${userId}/credential/invoices/${credentialFeeDefinitionId}/send`,
      null,
      null,
      ApiVersions.unversioned
    ).toPromise();
}

type AutomationResults = {
  toComplete: PersonScenarioRequirement[];
  notComplete: PersonScenarioRequirement[];
};

type MarkScenarioComplete = {
  isVerified?: boolean;
  note?: string;
};

type CheckSubmitted = {
  nameOnCheck: string;
  checkNumber: string;
  comment: string;
  personInvoiceId: string;
  personRequirementId?: string;
  personScenarioId?: string;
};
